<template>
  <div v-if="authChecked && !isAuthorized" class="unauthorized-container">
    <div class="unauthorized-message">
      <h1>Unauthorized</h1>
      <p>This application requires authentication. Please access it through the main Sybil application.</p>
    </div>
  </div>
  <template v-else-if="showContent">
    <Screen v-if="screening" />
    <template v-else-if="_isPC">
      <router-view />
    </template>
    <Mobile v-else />
  </template>
  <div v-else />
</template>

<script lang="ts" setup>
import { onMounted, ref, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useScreenStore, useMainStore, useSnapshotStore, useSlidesStore } from '@/store'
import { useGlobalStore } from '@/stores/global'
import { LOCALSTORAGE_KEY_DISCARDED_DB } from '@/configs/storage'
import { deleteDiscardedDB } from '@/utils/database'
import { isPC } from '@/utils/common'
import type { Slide } from '@/types/slides'
import api from '@/services'
import { api as axiosApi } from '@/services/api'
import { SlidoAuthService } from '@/utils/auth'

import Editor from './views/Editor/index.vue'
import Screen from './views/Screen/index.vue'
import Mobile from './views/Mobile/index.vue'

const router = useRouter()
const route = useRoute()
const _isPC = true
// Check if auth token exists on mount to prevent flashing
const hasToken = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search)
  return !!urlParams.get('auth_token')
}
const isAuthorized = ref(hasToken())
const authChecked = ref(false)

const mainStore = useMainStore()
const slidesStore = useSlidesStore()
const snapshotStore = useSnapshotStore()
const globalStore = useGlobalStore()
const { databaseId } = storeToRefs(mainStore)
const { slides } = storeToRefs(slidesStore)
const { screening } = storeToRefs(useScreenStore())

// Only the Editor needs the PPT slides store to be populated.
// All other routes (reports, templates, template viewer) should render
// immediately once auth is confirmed, regardless of mock slide load timing.
const showContent = computed(() => {
  if (!isAuthorized.value) return false
  if (route.path === '/editor') return slides.value.length > 0
  return true
})

function notifyParentLoading(loading: boolean) {
  if (window.self !== window.top) {
    window.parent.postMessage({ type: 'slido-loading-state', loading }, '*')
  }
}

watch([slides, authChecked, isAuthorized], () => {
  if (authChecked.value && !isAuthorized.value) {
    notifyParentLoading(false)
    return
  }

  notifyParentLoading(slides.value.length === 0)
}, { immediate: true })


onMounted(async () => {
  const urlParams = new URLSearchParams(window.location.search)
  const authToken = urlParams.get('auth_token')
  const targetPath = urlParams.get('path')
  
  if (authToken) {
    SlidoAuthService.setAuthToken(authToken)
    urlParams.delete('auth_token')
    const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '')
    window.history.replaceState({}, '', newUrl)
    
    // Verify the authentication token with the backend
    try {
      const response = await axiosApi.get('/users/verify-token')
      
      if (response.status === 200) {
        isAuthorized.value = true
      }
      else {
        isAuthorized.value = false
      }
    }
    catch (error) {
      console.error('Failed to verify authentication:', error)
      isAuthorized.value = false
    }
    finally {
      authChecked.value = true
    }
  }
  else {
    const isInIframe = window.self !== window.top
    if (isInIframe) {
      try {
        const response = await axiosApi.get('/users/verify-token')
        if (response.status === 200) {
          isAuthorized.value = true
        }
        else {
          isAuthorized.value = false
        }
      }
      catch (error) {
        isAuthorized.value = false
      }
      finally {
        authChecked.value = true
      }
    }
    else {
      isAuthorized.value = false
      authChecked.value = true
      return
    }
  }
  
  if (targetPath) {
    router.push(targetPath)
  }

  api.getFileData('slides').then((slides: Slide[]) => {
    slidesStore.setSlides(slides)
  })
  api.getFileData('layouts').then((slides: Slide[]) => {
    slidesStore.setLayouts(slides)
  })

  await deleteDiscardedDB()
  snapshotStore.initSnapshotDatabase()
})

// When the application is unloaded, record the current indexedDB database ID in localStorage for later database cleanup
window.addEventListener('unload', () => {
  const discardedDB = localStorage.getItem(LOCALSTORAGE_KEY_DISCARDED_DB)
  const discardedDBList: string[] = discardedDB ? JSON.parse(discardedDB) : []

  discardedDBList.push(databaseId.value)

  const newDiscardedDB = JSON.stringify(discardedDBList)
  localStorage.setItem(LOCALSTORAGE_KEY_DISCARDED_DB, newDiscardedDB)
})
</script>

<style lang="scss">
#app {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.unauthorized-container {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9fafb;
}

.unauthorized-message {
  text-align: center;
  padding: 2rem;
  max-width: 500px;
  
  h1 {
    font-size: 2rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1rem;
    color: #6b7280;
    line-height: 1.5;
  }
}
</style>
